import React, { forwardRef } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { DriverCardDay, DriverCardEventType, DriverCardSummary } from '../pages/drivers/use-driver-card';
import { Formatting } from '../utils';
import { PaymentType, PriceType, TaskType } from 'types';

const PrintTable = styled.table`
	width: 100%;
	page-break-inside: auto;

	tr {
		page-break-inside: avoid;
		page-break-after: auto;

		&.data:nth-child(2n) {
			background: #eee;
		}

		&.sectionHeader > th {
			font-size: 12px;
			font-weight: 500;
		}

		> td,
		> th {
			padding: 4px 8px;
			text-align: center;
			border: 1px solid #000;
		}

		> th {
			font-size: 14px;
		}

		> td {
			font-size: 10px;
		}
	}
`;

interface DriverCardPrintProps {
	driver: { name: string; surname: string } | null | undefined;
	days: DriverCardDay[];
	summary: DriverCardSummary;
	from: moment.Moment;
	to: moment.Moment;
}

export const DriverCardPrint = forwardRef<HTMLDivElement, DriverCardPrintProps>(
	({ driver, days, summary, from, to }, ref) => {
		if (!driver || !days || !summary) return <div ref={ref} />;

		const { advances, payments, spent, settlements } = summary;

		return (
			<div className="print" ref={ref}>
				<h2 style={{ textAlign: 'center', marginBottom: 4 }}>
					Karta Kierowcy — {driver.name} {driver.surname}
				</h2>
				<p style={{ textAlign: 'center', marginBottom: 16 }}>
					{from.format('DD.MM.YYYY')} — {to.format('DD.MM.YYYY')}
				</p>

				{days.length === 0 && <p>Brak aktywności w wybranym zakresie.</p>}

				{days.map((day) => {
					const deliveries = day.events.filter(
						(e) =>
							(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
							(e as any).task.type === TaskType.delivery
					);
					const claimings = day.events.filter(
						(e) =>
							(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
							(e as any).task.type === TaskType.claiming
					);
					const replacements = day.events.filter(
						(e) =>
							(e.type === DriverCardEventType.classicOrder || e.type === DriverCardEventType.looseOrder) &&
							(e as any).task.type === TaskType.replacement
					);
					const expenses = day.events.filter((e) => e.type === DriverCardEventType.expense);
					const daySettlements = day.events.filter((e) => e.type === DriverCardEventType.settlement);

					const renderOrderRow = (e: any, i: number) => {
						const task = e.task;
						const price = task.order.priceType === PriceType.gross ? task.order.grossPrice : task.order.netPrice;
						const paymentType =
							task.order.paymentType === PaymentType.cash || task.order.wantedPaymentType === PaymentType.cash
								? 'Gotówka'
								: 'Przelew';
						return (
							<tr key={e.key} className="data">
								<td>{i + 1}</td>
								<td>
									{moment(task.takenAt).format('HH:mm')} – {moment(task.doneAt).format('HH:mm')}
								</td>
								<td>{task.order.address}</td>
								<td>{paymentType}</td>
								<td>{Formatting.money(price)}</td>
								<td>{task.order.advance ? Formatting.money(task.order.advance) : 'Brak'}</td>
							</tr>
						);
					};

					const orderHeaderRow = (
						<tr className="sectionHeader">
							<th>L.P.</th>
							<th>Godzina</th>
							<th>Adres</th>
							<th>Płatność</th>
							<th>Kwota</th>
							<th>Zaliczka</th>
						</tr>
					);

					return (
						<div key={day.key} style={{ marginTop: 16, pageBreakInside: 'avoid' }}>
							<PrintTable>
								<tbody>
									{/* Day heading */}
									<tr>
										<th colSpan={6}>{day.date.format('dddd, D MMMM YYYY')}</th>
									</tr>

									{/* Podstawienia */}
									<tr>
										<th colSpan={6}>Podstawienia</th>
									</tr>
									{deliveries.length > 0 && orderHeaderRow}
									{deliveries.map((e, i) => renderOrderRow(e, i))}

									{/* Odbiory */}
									<tr>
										<th colSpan={6}>Odbiory</th>
									</tr>
									{claimings.length > 0 && orderHeaderRow}
									{claimings.map((e, i) => renderOrderRow(e, i))}

									{/* Wymiany */}
									<tr>
										<th colSpan={6}>Wymiany</th>
									</tr>
									{replacements.length > 0 && orderHeaderRow}
									{replacements.map((e, i) => renderOrderRow(e, i))}

									{/* Wydatki */}
									<tr>
										<th colSpan={6}>Wydatki</th>
									</tr>
									{expenses.length > 0 && (
										<tr className="sectionHeader">
											<th>L.P.</th>
											<th>Godzina</th>
											<th>Typ i nazwa</th>
											<th>Kwota</th>
											<th>Podatek</th>
											<th>Przebieg</th>
										</tr>
									)}
									{expenses.map((e: any, i) => (
										<tr key={e.key} className="data">
											<td>{i + 1}</td>
											<td>{moment(e.expense.created).format('HH:mm')}</td>
											<td>
												{e.expense.type?.name} – {e.expense.name}
											</td>
											<td>
												{Formatting.money(e.expense.value)} {e.expense.value_type ? 'Brutto' : 'Netto'}
											</td>
											<td>{e.expense.tax}%</td>
											<td>{e.expense.mileage ? Formatting.insertSpaces(e.expense.mileage) : '-'}</td>
										</tr>
									))}

									{/* Wydania gotówki */}
									<tr>
										<th colSpan={6}>Wydania gotówki</th>
									</tr>
									{daySettlements.length > 0 && (
										<tr className="sectionHeader">
											<th>L.P.</th>
											<th>Godzina</th>
											<th>Kwota</th>
											<th colSpan={3}>Odebrał</th>
										</tr>
									)}
									{daySettlements.map((e: any, i) => (
										<tr key={e.key} className="data">
											<td>{i + 1}</td>
											<td>{moment(e.settlement.created).format('HH:mm')}</td>
											<td>{Formatting.money(e.settlement.value)}</td>
											<td colSpan={3}>
												{e.settlement.created_by?.name} {e.settlement.created_by?.surname}
											</td>
										</tr>
									))}
								</tbody>
							</PrintTable>
						</div>
					);
				})}

				{/* Range summary */}
				<PrintTable style={{ marginTop: 24 }}>
					<tbody>
						<tr>
							<th colSpan={6}>Podsumowanie okresu</th>
						</tr>
						<tr>
							<td colSpan={3}>Pobrane od klientów: {Formatting.money(advances + payments)}</td>
							<td colSpan={3}>Wydane: {Formatting.money(spent + settlements)}</td>
						</tr>
						<tr>
							<td>Zaliczki: {Formatting.money(advances)}</td>
							<td colSpan={2}>Zapłaty: {Formatting.money(payments)}</td>
							<td>Wydatki: {Formatting.money(spent)}</td>
							<td colSpan={2}>Pobrania gotówki: {Formatting.money(settlements)}</td>
						</tr>
						<tr>
							<th colSpan={6}>Balans: {Formatting.money(advances + payments - spent - settlements)}</th>
						</tr>
					</tbody>
				</PrintTable>
			</div>
		);
	}
);
